/**
 * PhoneSystem — Telefonanlage UI Komponenten (Issue #60)
 *
 * Enthält:
 * - TelefonChannel: Hauptansicht im Kommunikationsmodul (Call Log + Detail)
 * - PhoneDialer: Wähltastatur + Quick-Kontakte
 * - ActiveCallView: Laufendes Gespräch mit Steuerung
 * - IncomingCallPopup: Popup bei eingehendem Anruf
 * - CallDetailPanel: Detail-Ansicht eines Anrufs (Notizen, KI, Aufnahme)
 * - DurchwahlenPanel: Extension-Verwaltung
 *
 * Alle Features nach Aircall/HubSpot Best Practices:
 * - Click-to-Call (Browser-basiert)
 * - Eingehende Anrufe als Popup mit Kontakt-Lookup
 * - Durchwahlen für Mitarbeiter
 * - Anrufhistorie & Aufzeichnung
 * - KI-Vorschläge nach Gesprächsende
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Phone, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing, PhoneOff, PhoneForwarded,
  Mic, MicOff, Pause, Play, Circle, Square,
  User, Users, Building2, FileText, Calendar, Mail,
  Clock, Timer, Hash, Star, Tag,
  ChevronRight, ChevronDown, X, Search, Plus, MoreVertical,
  MessageSquare, Voicemail, Volume2, VolumeX,
  ArrowRight, ArrowLeft, RotateCcw, Trash2,
  CheckCircle2, AlertCircle, Sparkles, Zap, ListTodo,
  Bot, Headphones, Settings, Shield,
  Delete,
} from 'lucide-react';
import { toast } from 'sonner';
import type { PhoneCall as PhoneCallType, PhoneExtension, ActiveCall, AISuggestedAction } from '@/hooks/usePhoneSystem';

// ─── Helper ────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}:${String(rm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatPhoneNumber(num: string): string {
  if (!num) return '';
  // German phone formatting
  const cleaned = num.replace(/\s/g, '');
  if (cleaned.startsWith('+49')) {
    const local = cleaned.slice(3);
    if (local.length >= 10) {
      return `+49 ${local.slice(0, 3)} ${local.slice(3, 7)} ${local.slice(7)}`;
    }
    return `+49 ${local}`;
  }
  return num;
}

function formatCallTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function formatCallDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Heute';
    if (days === 1) return 'Gestern';
    if (days < 7) return d.toLocaleDateString('de-DE', { weekday: 'long' });
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
}

function getCallIcon(call: PhoneCallType) {
  if (call.status === 'missed' || call.status === 'no-answer') return PhoneMissed;
  if (call.status === 'voicemail') return Voicemail;
  if (call.direction === 'inbound') return PhoneIncoming;
  return PhoneOutgoing;
}

function getCallColor(call: PhoneCallType): string {
  if (call.status === 'missed' || call.status === 'no-answer') return '#EF4444';
  if (call.status === 'voicemail') return '#F59E0B';
  if (call.status === 'busy' || call.status === 'failed') return '#6B7280';
  if (call.direction === 'inbound') return '#22C55E';
  return '#3B82F6';
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    'ringing': 'Klingelt',
    'in-progress': 'Aktiv',
    'completed': 'Beendet',
    'missed': 'Verpasst',
    'busy': 'Besetzt',
    'no-answer': 'Keine Antwort',
    'canceled': 'Abgebrochen',
    'failed': 'Fehlgeschlagen',
    'voicemail': 'Sprachnachricht',
  };
  return labels[status] || status;
}

// ─── Incoming Call Popup ───────────────────────────────────────────────────

export function IncomingCallPopup({
  call,
  onAnswer,
  onReject,
}: {
  call: ActiveCall;
  onAnswer: () => void;
  onReject: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Play ringtone effect (visual pulse)
  const ringPulse = elapsed % 2 === 0;

  return (
    <div className="fixed top-4 right-4 z-[200] animate-in slide-in-from-top-2 fade-in duration-300" role="alertdialog" aria-label="Eingehender Anruf">
      <div className={`w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all ${ringPulse ? 'ring-2 ring-green-400 ring-opacity-75' : ''}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-5 py-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <PhoneIncoming className="w-5 h-5 animate-pulse" />
            <span className="text-sm font-semibold uppercase tracking-wider opacity-90">Eingehender Anruf</span>
          </div>
          <div className="text-lg font-bold truncate">
            {call.contactName || 'Unbekannter Anrufer'}
          </div>
          <div className="text-sm opacity-80 font-mono">
            {formatPhoneNumber(call.phoneNumber)}
          </div>
        </div>

        {/* Contact info */}
        {call.contactName && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <User className="w-5 h-5 text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{call.contactName}</div>
              <div className="text-xs text-gray-500">Kontakt erkannt</div>
            </div>
          </div>
        )}

        {/* Timer */}
        <div className="px-5 py-2 text-center">
          <span className="text-sm text-gray-500 font-mono">Klingelt seit {elapsed}s</span>
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 flex gap-3">
          <button
            onClick={onReject}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition-colors"
            aria-label="Anruf ablehnen"
          >
            <PhoneOff className="w-5 h-5" />
            Ablehnen
          </button>
          <button
            onClick={onAnswer}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors animate-pulse"
            aria-label="Anruf annehmen"
          >
            <Phone className="w-5 h-5" />
            Annehmen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Active Call View ──────────────────────────────────────────────────────

export function ActiveCallView({
  call,
  onEnd,
  onMute,
  onHold,
  onRecord,
  onTransfer,
  extensions,
}: {
  call: ActiveCall;
  onEnd: () => void;
  onMute: () => void;
  onHold: () => void;
  onRecord: () => void;
  onTransfer: (ext: string) => void;
  extensions: PhoneExtension[];
}) {
  const [elapsed, setElapsed] = useState(0);
  const [showTransfer, setShowTransfer] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    const start = new Date(call.startedAt).getTime();
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [call.startedAt]);

  return (
    <div className="fixed inset-0 z-[180] bg-black/50 flex items-center justify-center" role="dialog" aria-label="Aktiver Anruf">
      <div className="w-96 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Status bar */}
        <div className={`px-6 py-4 text-white ${call.isOnHold ? 'bg-amber-500' : call.status === 'ringing' ? 'bg-blue-500' : 'bg-green-600'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold uppercase tracking-wider opacity-90">
              {call.status === 'ringing' ? 'Verbinde...' : call.isOnHold ? 'Gehalten' : 'Aktiver Anruf'}
            </span>
            <span className="text-sm font-mono tabular-nums">{formatDuration(elapsed)}</span>
          </div>
          <div className="text-xl font-bold truncate">{call.contactName || 'Unbekannt'}</div>
          <div className="text-sm opacity-80 font-mono">{formatPhoneNumber(call.phoneNumber)}</div>
          {call.direction === 'outbound' && <span className="text-xs opacity-70">Ausgehend</span>}
        </div>

        {/* Call controls */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <button
              onClick={onMute}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors ${call.isMuted ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              aria-label={call.isMuted ? 'Stummschaltung aufheben' : 'Stummschalten'}
            >
              {call.isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              <span className="text-[11px] font-medium">{call.isMuted ? 'Stumm' : 'Stumm'}</span>
            </button>
            <button
              onClick={onHold}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors ${call.isOnHold ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              aria-label={call.isOnHold ? 'Fortsetzen' : 'Halten'}
            >
              {call.isOnHold ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              <span className="text-[11px] font-medium">{call.isOnHold ? 'Fortsetzen' : 'Halten'}</span>
            </button>
            <button
              onClick={onRecord}
              className={`flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors ${call.isRecording ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              aria-label={call.isRecording ? 'Aufnahme stoppen' : 'Aufnahme starten'}
            >
              {call.isRecording ? <Square className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
              <span className="text-[11px] font-medium">{call.isRecording ? 'Aufn. an' : 'Aufnehmen'}</span>
            </button>
          </div>

          {/* Transfer button */}
          <div className="mb-4">
            <button
              onClick={() => setShowTransfer(!showTransfer)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 rounded-xl text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <PhoneForwarded className="w-4 h-4" />
                <span>Weiterleiten</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${showTransfer ? 'rotate-180' : ''}`} />
            </button>
            {showTransfer && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {extensions.filter(e => e.status === 'available' && e.is_active).map(ext => (
                  <button
                    key={ext.id}
                    onClick={() => { onTransfer(ext.extension); setShowTransfer(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
                  >
                    <div className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="font-medium">{ext.user_name}</span>
                    <span className="text-gray-400 ml-auto">Ext. {ext.extension}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quick note */}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notizen zum Gespräch..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl resize-none h-20 focus:outline-none focus:ring-2 focus:ring-green-500/30"
          />
        </div>

        {/* End call button */}
        <div className="px-6 pb-6">
          <button
            onClick={onEnd}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold text-base transition-colors"
            aria-label="Anruf beenden"
          >
            <PhoneOff className="w-5 h-5" />
            Auflegen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Phone Dialer ──────────────────────────────────────────────────────────

export function PhoneDialer({
  onCall,
  onClose,
  recentContacts,
}: {
  onCall: (number: string, name?: string) => void;
  onClose: () => void;
  recentContacts?: { name: string; number: string }[];
}) {
  const [number, setNumber] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const dialPad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['*', '0', '#'],
  ];

  const subLabels: Record<string, string> = {
    '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL',
    '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ',
    '0': '+',
  };

  const handleDial = (digit: string) => {
    setNumber(prev => prev + digit);
  };

  const handleDelete = () => {
    setNumber(prev => prev.slice(0, -1));
  };

  const handleCall = () => {
    if (number.length >= 3) {
      onCall(number);
    }
  };

  const quickContacts = recentContacts || [
    { name: 'Allianz Versicherung', number: '+4923516789012' },
    { name: 'ADAC Pannenhilfe', number: '+498005101112' },
    { name: 'AutoFix Werkstatt', number: '+4923519876543' },
  ];

  return (
    <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center" role="dialog" aria-label="Telefon-Wähler">
      <div className="w-80 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Anrufen</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Number display */}
        <div className="px-5 py-4">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
            <input
              ref={inputRef}
              type="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Nummer eingeben"
              className="flex-1 bg-transparent text-lg font-mono text-gray-900 placeholder-gray-400 outline-none"
              aria-label="Telefonnummer eingeben"
            />
            {number && (
              <button onClick={handleDelete} className="p-1 hover:bg-gray-200 rounded">
                <Delete className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>
        </div>

        {/* Dial pad */}
        <div className="px-5 pb-3">
          <div className="grid grid-cols-3 gap-2">
            {dialPad.flat().map(digit => (
              <button
                key={digit}
                onClick={() => handleDial(digit)}
                className="flex flex-col items-center justify-center h-14 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              >
                <span className="text-xl font-semibold text-gray-800">{digit}</span>
                {subLabels[digit] && (
                  <span className="text-[9px] font-medium text-gray-400 tracking-widest">{subLabels[digit]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Call button */}
        <div className="px-5 pb-4">
          <button
            onClick={handleCall}
            disabled={number.length < 3}
            className="w-full flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl font-bold transition-colors"
            aria-label="Anrufen"
          >
            <Phone className="w-5 h-5" />
            Anrufen
          </button>
        </div>

        {/* Quick contacts */}
        {quickContacts.length > 0 && (
          <div className="px-5 pb-4 border-t border-gray-100 pt-3">
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Schnellwahl</div>
            {quickContacts.map((c, i) => (
              <button
                key={i}
                onClick={() => onCall(c.number, c.name)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                  <div className="text-xs text-gray-500 font-mono">{formatPhoneNumber(c.number)}</div>
                </div>
                <Phone className="w-4 h-4 text-green-500" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Call Detail Panel (right sidebar in Kommunikation) ────────────────────

export function CallDetailPanel({
  call,
  onUpdateNotes,
  onRequestCallback,
  onMarkCallbackDone,
  onClose,
}: {
  call: PhoneCallType;
  onUpdateNotes: (callId: string, notes: string) => void;
  onRequestCallback: (callId: string, note: string) => void;
  onMarkCallbackDone: (callId: string) => void;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState(call.notes || '');
  const [callbackNote, setCallbackNote] = useState('');
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'ai' | 'aufnahme'>('details');

  const Icon = getCallIcon(call);
  const statusColor = getCallColor(call);

  const handleSaveNotes = useCallback(() => {
    onUpdateNotes(call.id, notes);
    toast.success('Notizen gespeichert');
  }, [call.id, notes, onUpdateNotes]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${statusColor}15` }}>
            <Icon className="w-5 h-5" style={{ color: statusColor }} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{call.contact_name || formatPhoneNumber(call.direction === 'inbound' ? call.from_number : call.to_number)}</div>
            <div className="text-xs text-gray-500">
              {getStatusLabel(call.status)} &middot; {formatCallDate(call.started_at)} {formatCallTime(call.started_at)}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg flex-shrink-0">
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        {(['details', 'ai', 'aufnahme'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors border-b-2 ${
              activeTab === tab ? 'text-purple-600 border-purple-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab === 'details' ? 'Details' : tab === 'ai' ? 'KI-Assistent' : 'Aufnahme'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'details' && (
          <div className="p-4 space-y-4">
            {/* Call info */}
            <div className="space-y-2">
              <InfoRow label="Richtung" value={call.direction === 'inbound' ? 'Eingehend' : 'Ausgehend'} icon={call.direction === 'inbound' ? <PhoneIncoming className="w-3.5 h-3.5" /> : <PhoneOutgoing className="w-3.5 h-3.5" />} />
              <InfoRow label="Von" value={formatPhoneNumber(call.from_number)} icon={<Phone className="w-3.5 h-3.5" />} />
              <InfoRow label="An" value={formatPhoneNumber(call.to_number)} icon={<Phone className="w-3.5 h-3.5" />} />
              {call.extension && <InfoRow label="Durchwahl" value={`Ext. ${call.extension}`} icon={<Hash className="w-3.5 h-3.5" />} />}
              <InfoRow label="Dauer" value={call.duration_seconds > 0 ? formatDuration(call.duration_seconds) : '—'} icon={<Timer className="w-3.5 h-3.5" />} />
              {call.user_name && <InfoRow label="Mitarbeiter" value={call.user_name} icon={<User className="w-3.5 h-3.5" />} />}
              {call.quality_rating && (
                <InfoRow
                  label="Bewertung"
                  value={<div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= call.quality_rating! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />)}</div>}
                  icon={<Star className="w-3.5 h-3.5" />}
                />
              )}
            </div>

            {/* Tags */}
            {call.tags && call.tags.length > 0 && (
              <div>
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tags</div>
                <div className="flex flex-wrap gap-1.5">
                  {call.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{tag}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Callback request */}
            {call.callback_requested && !call.callback_completed_at && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <RotateCcw className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Rückrufbitte</span>
                </div>
                {call.callback_note && <p className="text-xs text-amber-700 mb-2">{call.callback_note}</p>}
                <button
                  onClick={() => onMarkCallbackDone(call.id)}
                  className="text-xs bg-amber-600 text-white px-3 py-1 rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Als erledigt markieren
                </button>
              </div>
            )}

            {/* Notes */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Notizen</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Gesprächsnotizen..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl resize-none h-24 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
              />
              <button
                onClick={handleSaveNotes}
                className="mt-1.5 text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Speichern
              </button>
            </div>

            {/* Request callback */}
            {!call.callback_requested && (
              <div>
                {!showCallbackForm ? (
                  <button
                    onClick={() => setShowCallbackForm(true)}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" /> Rückrufbitte erstellen
                  </button>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                    <textarea
                      value={callbackNote}
                      onChange={(e) => setCallbackNote(e.target.value)}
                      placeholder="Rückruf-Grund..."
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none h-16 focus:outline-none focus:ring-2 focus:ring-purple-500/30"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => { onRequestCallback(call.id, callbackNote); setShowCallbackForm(false); toast.success('Rückrufbitte erstellt'); }}
                        className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg"
                      >
                        Erstellen
                      </button>
                      <button onClick={() => setShowCallbackForm(false)} className="text-xs text-gray-500 px-3 py-1.5">Abbrechen</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Transfer info */}
            {call.transferred_from && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <PhoneForwarded className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-blue-800">Weitergeleitet{call.transfer_reason ? `: ${call.transfer_reason}` : ''}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ai' && (
          <div className="p-4 space-y-4">
            {/* AI Summary */}
            {call.ai_summary ? (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-800">KI-Zusammenfassung</span>
                </div>
                <p className="text-sm text-purple-900 leading-relaxed">{call.ai_summary}</p>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400">
                <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Keine KI-Zusammenfassung verfügbar</p>
                <p className="text-xs mt-1">Wird nach Gesprächsende automatisch erstellt</p>
              </div>
            )}

            {/* AI Suggested Actions */}
            {call.ai_suggested_actions && call.ai_suggested_actions.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-gray-900">Vorgeschlagene Aktionen</span>
                </div>
                <div className="space-y-2">
                  {call.ai_suggested_actions.map((action, i) => (
                    <AIActionCard key={i} action={action} />
                  ))}
                </div>
              </div>
            )}

            {/* Transcription */}
            {call.transcription && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">Transkription</span>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 leading-relaxed">
                  {call.transcription}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'aufnahme' && (
          <div className="p-4 space-y-4">
            {call.recording_url ? (
              <>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Headphones className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">Gesprächsaufzeichnung</div>
                      <div className="text-xs text-gray-500">
                        Dauer: {call.recording_duration_seconds ? formatDuration(call.recording_duration_seconds) : '—'}
                      </div>
                    </div>
                  </div>
                  {/* Audio player placeholder */}
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <button className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center hover:bg-purple-700 transition-colors">
                        <Play className="w-4 h-4 text-white ml-0.5" />
                      </button>
                      <div className="flex-1">
                        <div className="w-full h-1.5 bg-gray-200 rounded-full">
                          <div className="w-0 h-1.5 bg-purple-600 rounded-full" />
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 font-mono tabular-nums">
                        0:00 / {call.recording_duration_seconds ? formatDuration(call.recording_duration_seconds) : '0:00'}
                      </span>
                    </div>
                  </div>
                </div>

                {call.transcription && (
                  <div>
                    <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Transkription</div>
                    <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 leading-relaxed max-h-60 overflow-y-auto">
                      {call.transcription}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Volume2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Keine Aufzeichnung vorhanden</p>
                <p className="text-xs mt-1">Aufzeichnung kann beim nächsten Anruf aktiviert werden</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AI Action Card ────────────────────────────────────────────────────────

function AIActionCard({ action }: { action: AISuggestedAction }) {
  const iconMap = {
    termin: Calendar,
    aufgabe: ListTodo,
    email: Mail,
    notiz: FileText,
    rueckruf: RotateCcw,
  };
  const colorMap = {
    termin: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500', btn: 'bg-blue-600 hover:bg-blue-700' },
    aufgabe: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500', btn: 'bg-amber-600 hover:bg-amber-700' },
    email: { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-500', btn: 'bg-green-600 hover:bg-green-700' },
    notiz: { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'text-gray-500', btn: 'bg-gray-600 hover:bg-gray-700' },
    rueckruf: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-500', btn: 'bg-purple-600 hover:bg-purple-700' },
  };

  const Icon = iconMap[action.type] || Zap;
  const colors = colorMap[action.type] || colorMap.notiz;

  const handleExecute = () => {
    toast.success(`${action.titel} — wird ausgeführt`, { description: 'Feature wird mit Twilio-Integration verfügbar' });
  };

  return (
    <div className={`${colors.bg} rounded-xl p-3 border border-gray-200/50`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${colors.icon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${colors.text}`}>{action.titel}</div>
          {action.beschreibung && <div className="text-xs text-gray-500 mt-0.5">{action.beschreibung}</div>}
          {action.prioritaet && (
            <span className={`inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
              action.prioritaet === 'hoch' ? 'bg-red-100 text-red-700' :
              action.prioritaet === 'mittel' ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-600'
            }`}>{action.prioritaet}</span>
          )}
        </div>
        <button
          onClick={handleExecute}
          className={`${colors.btn} text-white text-xs px-2.5 py-1 rounded-lg font-medium flex-shrink-0 transition-colors`}
        >
          Ausführen
        </button>
      </div>
    </div>
  );
}

// ─── Info Row (for detail panel) ───────────────────────────────────────────

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="text-gray-400">{icon}</div>
      <div className="text-xs text-gray-500 w-20 flex-shrink-0">{label}</div>
      <div className="text-sm text-gray-900 font-medium flex-1">{value}</div>
    </div>
  );
}

// ─── Call Log List (for Kommunikation telefon channel) ─────────────────────

export function CallLogList({
  calls,
  selectedCallId,
  onSelectCall,
  onMakeCall,
  filter,
  onFilterChange,
}: {
  calls: PhoneCallType[];
  selectedCallId?: string;
  onSelectCall: (call: PhoneCallType) => void;
  onMakeCall: () => void;
  filter: 'alle' | 'eingehend' | 'ausgehend' | 'verpasst';
  onFilterChange: (f: 'alle' | 'eingehend' | 'ausgehend' | 'verpasst') => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let result = calls;

    // Filter by direction/status
    if (filter === 'eingehend') result = result.filter(c => c.direction === 'inbound');
    if (filter === 'ausgehend') result = result.filter(c => c.direction === 'outbound');
    if (filter === 'verpasst') result = result.filter(c => c.status === 'missed' || c.status === 'no-answer' || c.status === 'voicemail');

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.contact_name?.toLowerCase().includes(q) ||
        c.from_number.includes(q) ||
        c.to_number.includes(q) ||
        c.notes?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [calls, filter, search]);

  // Group by date
  const grouped = useMemo(() => {
    const groups: { date: string; calls: PhoneCallType[] }[] = [];
    let currentDate = '';

    for (const call of filtered) {
      const date = formatCallDate(call.started_at);
      if (date !== currentDate) {
        currentDate = date;
        groups.push({ date, calls: [call] });
      } else {
        groups[groups.length - 1].calls.push(call);
      }
    }
    return groups;
  }, [filtered]);

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-3 py-2 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Anrufe durchsuchen..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/30"
            aria-label="Anrufe durchsuchen"
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="px-3 pb-2 flex gap-1 flex-shrink-0">
        {(['alle', 'eingehend', 'ausgehend', 'verpasst'] as const).map(f => (
          <button
            key={f}
            onClick={() => onFilterChange(f)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              filter === f ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {f === 'alle' ? 'Alle' : f === 'eingehend' ? 'Eingehend' : f === 'ausgehend' ? 'Ausgehend' : 'Verpasst'}
          </button>
        ))}
      </div>

      {/* Call list */}
      <div className="flex-1 overflow-y-auto">
        {grouped.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Phone className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="text-sm text-gray-500">Keine Anrufe gefunden</p>
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.date}>
              <div className="px-4 py-2 bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
                {group.date}
              </div>
              {group.calls.map(call => {
                const Icon = getCallIcon(call);
                const color = getCallColor(call);
                const displayName = call.contact_name || formatPhoneNumber(call.direction === 'inbound' ? call.from_number : call.to_number);
                const isSelected = call.id === selectedCallId;

                return (
                  <button
                    key={call.id}
                    onClick={() => onSelectCall(call)}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors text-left border-b border-gray-50 ${
                      isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                    aria-label={`${call.direction === 'inbound' ? 'Eingehender' : 'Ausgehender'} Anruf: ${displayName}`}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold truncate ${
                          (call.status === 'missed' || call.status === 'no-answer') ? 'text-red-600' : 'text-gray-900'
                        }`}>{displayName}</span>
                        {call.callback_requested && !call.callback_completed_at && (
                          <RotateCcw className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        )}
                        {call.recording_url && (
                          <Headphones className="w-3 h-3 text-purple-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{getStatusLabel(call.status)}</span>
                        {call.duration_seconds > 0 && (
                          <>
                            <span className="text-gray-300">&middot;</span>
                            <span>{formatDuration(call.duration_seconds)}</span>
                          </>
                        )}
                        {call.extension && (
                          <>
                            <span className="text-gray-300">&middot;</span>
                            <span>Ext. {call.extension}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-[11px] text-gray-400 flex-shrink-0 tabular-nums">
                      {formatCallTime(call.started_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* New call button */}
      <div className="px-3 py-3 border-t border-gray-200 flex-shrink-0">
        <button
          onClick={onMakeCall}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm transition-colors"
          aria-label="Neuen Anruf starten"
        >
          <Phone className="w-4 h-4" />
          Neuer Anruf
        </button>
      </div>
    </div>
  );
}

// ─── Durchwahlen Panel ─────────────────────────────────────────────────────

export function DurchwahlenPanel({
  extensions,
}: {
  extensions: PhoneExtension[];
}) {
  const statusColors: Record<string, { bg: string; text: string; label: string }> = {
    available: { bg: 'bg-green-100', text: 'text-green-700', label: 'Verfügbar' },
    busy: { bg: 'bg-red-100', text: 'text-red-700', label: 'Im Gespräch' },
    dnd: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Nicht stören' },
    offline: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Offline' },
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-900">Durchwahlen</h3>
        <span className="text-xs text-gray-400">{extensions.filter(e => e.is_active).length} aktiv</span>
      </div>
      {extensions.map(ext => {
        const s = statusColors[ext.status] || statusColors.offline;
        return (
          <div key={ext.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className={`w-10 h-10 rounded-full ${s.bg} flex items-center justify-center`}>
              <span className="text-sm font-bold" style={{ color: s.text.replace('text-', '') }}>{ext.extension}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{ext.user_name}</div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${s.text} font-medium`}>{s.label}</span>
                {ext.voicemail_enabled && <Voicemail className="w-3 h-3 text-gray-400" />}
                {ext.forward_to_number && <PhoneForwarded className="w-3 h-3 text-blue-400" />}
              </div>
            </div>
            <div className="text-xs text-gray-400">Ext. {ext.extension}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Phone Stats Banner ────────────────────────────────────────────────────

export function PhoneStatsBanner({
  todayCount,
  missedCount,
  callbackCount,
  avgDuration,
}: {
  todayCount: number;
  missedCount: number;
  callbackCount: number;
  avgDuration: number;
}) {
  return (
    <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100 flex-shrink-0">
      <div className="flex items-center gap-4 overflow-x-auto">
        <StatBadge label="Heute" value={todayCount} color="purple" icon={<PhoneCall className="w-3.5 h-3.5" />} />
        <StatBadge label="Verpasst" value={missedCount} color={missedCount > 0 ? 'red' : 'gray'} icon={<PhoneMissed className="w-3.5 h-3.5" />} />
        <StatBadge label="Rückrufe" value={callbackCount} color={callbackCount > 0 ? 'amber' : 'gray'} icon={<RotateCcw className="w-3.5 h-3.5" />} />
        <StatBadge label="Ø Dauer" value={formatDuration(avgDuration)} color="blue" icon={<Timer className="w-3.5 h-3.5" />} />
      </div>
    </div>
  );
}

function StatBadge({ label, value, color, icon }: { label: string; value: React.ReactNode; color: string; icon: React.ReactNode }) {
  const colors: Record<string, string> = {
    purple: 'text-purple-700',
    red: 'text-red-600',
    amber: 'text-amber-600',
    blue: 'text-blue-600',
    gray: 'text-gray-400',
  };

  return (
    <div className="flex items-center gap-2 flex-shrink-0">
      <div className={colors[color] || colors.gray}>{icon}</div>
      <div>
        <div className={`text-sm font-bold ${colors[color] || colors.gray}`}>{value}</div>
        <div className="text-[10px] text-gray-500">{label}</div>
      </div>
    </div>
  );
}
